/**
 * <DrawerDisconnectBanner>: countdown overlay during drawer-disconnect grace.
 *
 * Per PRD §3.8 the drawer has a 10s reconnect window after their socket
 * drops mid-{wordPick, drawing}. The server emits DRAWER_DISCONNECT_GRACE
 * carrying `{ playerId, graceUntil }`; the client store mirrors that as
 * `game.drawerDisconnect`. This banner renders a wobbly paper notice with
 * a live countdown until the grace expires.
 *
 * DRAWER_RECONNECT (matching playerId) clears the store state; the banner
 * unmounts naturally. If the grace expires the server fires
 * PHASE_END(→reveal), the subsequent ROOM_STATE_SYNC clears the state and
 * the phase router routes to RevealPhaseShell.
 */

import { useEffect, useState } from "preact/hooks";
import { PaperBanner } from "./PaperBanner.js";

type DrawerDisconnectBannerProps = {
  drawerName: string;
  graceUntil: number;
};

export function DrawerDisconnectBanner({ drawerName, graceUntil }: DrawerDisconnectBannerProps) {
  // Force re-render every 500ms so the countdown text stays fresh. Cheaper
  // than 60Hz; the user only sees integer-second changes anyway.
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, []);

  const secondsRemaining = Math.max(0, Math.ceil((graceUntil - Date.now()) / 1000));
  // The store will clear this banner via DRAWER_RECONNECT or the next
  // ROOM_STATE_SYNC after PHASE_END(→reveal). If we hit 0 before either
  // arrives (clock skew or in-flight WS frame), render with "0s" rather
  // than hiding: the parent decides when to unmount.
  // Audit 2026-06-11: the bare countdown never said what it MEANT.
  const message = `${drawerName} dropped — ${secondsRemaining}s for them to come back`;

  // 24h "duration" stands in for "never". PaperBanner's warn kind would
  // otherwise auto-dismiss; the banner lifecycle should be controlled by
  // the store (DRAWER_RECONNECT or phase change clears it). Setting
  // `Number.MAX_SAFE_INTEGER` here was a footgun: Node's setTimeout clamps
  // beyond the 32-bit max and effectively fires at the next tick.
  const NEVER_DISMISS_MS = 24 * 60 * 60 * 1000;

  return (
    <div data-testid="drawer-disconnect-banner" data-seconds-remaining={secondsRemaining}>
      <PaperBanner kind="warn" message={message} duration={NEVER_DISMISS_MS} />
    </div>
  );
}
